import React, { useState } from "react";
import { Form, message } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import {
  useRawMaterialsQuery,
  useCreateRawMaterialMutation,
  useUpdateRawMaterialMutation,
  useDeleteRawMaterialMutation,
  useToggleRawMaterialStatusMutation,
  useSuppliersQuery,
  useSectionAccess,
} from "../../Services";
import { RecordStatus } from "../../Enums";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { PageHeader } from "../../components";
import {
  RawMaterialFormDrawer,
  StockHistoryDrawer,
  RawMaterialsFilters,
  RawMaterialsTable,
} from "./common";
import { RawMaterial, RawMaterialStock, RawMaterialAction } from "../../Types";

export const RawMaterialsPage = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [supplierId, setSupplierId] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<RawMaterial | null>(
    null,
  );
  const [form] = Form.useForm();
  const [formStocks, setFormStocks] = useState<RawMaterialStock[]>([]);

  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [historyMaterial, setHistoryMaterial] = useState<RawMaterial | null>(
    null,
  );

  const { data: suppliers = [] } = useSuppliersQuery();

  const {
    data: materialsList,
    isLoading,
    refetch,
  } = useRawMaterialsQuery(
    { page, limit: PAGE_SIZE, search, supplier_id: supplierId },
    { placeholderData: (prev) => prev },
  );

  const total: number = materialsList?.total ?? 0;

  const createMutation = useCreateRawMaterialMutation();
  const updateMutation = useUpdateRawMaterialMutation();
  const deleteMutation = useDeleteRawMaterialMutation();
  const toggleStatusMutation = useToggleRawMaterialStatusMutation();
  const { isAllowed } = useSectionAccess("raw_materials");

  const openDrawer = (material?: RawMaterial) => {
    if (material) {
      setEditingMaterial(material);
      form.setFieldsValue({
        name: material.name,
        code: material.code,
        unit: material.unit,
        alert_quantity: material.alert_quantity,
        suppliers: material.suppliers.map((s) => s.id),
      });
      setFormStocks(material.stocks);
    } else {
      setEditingMaterial(null);
      form.resetFields();
      setFormStocks([
        {
          batch_no: "BATCH-new",
          cost_price: 0,
          quantity: 0,
          expiry_date: null,
          is_default: 1,
        },
      ]);
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingMaterial(null);
    form.resetFields();
    setFormStocks([]);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (formStocks.length === 0) {
        message.error("At least one stock batch is required.");
        return;
      }

      for (let i = 0; i < formStocks.length; i++) {
        const batch = formStocks[i];
        if (!batch.batch_no.trim()) {
          message.error(`Batch No in batch #${i + 1} is required.`);
          return;
        }
        if (batch.cost_price <= 0) {
          message.error(
            `Cost Price in batch #${i + 1} must be greater than 0.`,
          );
          return;
        }
        if (batch.quantity < 0) {
          message.error(`Quantity in batch #${i + 1} cannot be negative.`);
          return;
        }
      }

      const payload = { ...values, stocks: formStocks };

      if (editingMaterial) {
        const res = await updateMutation.mutateAsync({
          id: editingMaterial.id,
          ...payload,
        });
        if (res?.success) {
          message.success("Raw material updated successfully.");
          closeDrawer();
        } else {
          message.error(res?.message || "Failed to update raw material.");
        }
      } else {
        const res = await createMutation.mutateAsync(payload);
        if (res?.success) {
          message.success("Raw material created successfully.");
          closeDrawer();
        } else {
          message.error(res?.message || "Failed to create raw material.");
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const addFormStock = () => {
    setFormStocks([
      ...formStocks,
      {
        batch_no: `BATCH-new-${formStocks.length}`,
        cost_price: 0,
        quantity: 0,
        expiry_date: null,
        is_default: formStocks.length === 0 ? 1 : 0,
      },
    ]);
  };

  const removeFormStock = (index: number) => {
    const updated = [...formStocks];
    const removed = updated.splice(index, 1)[0];
    if (removed && removed.is_default === 1 && updated.length > 0) {
      updated[0].is_default = 1;
    }
    setFormStocks(updated);
  };

  const updateFormStock = (
    index: number,
    key: keyof RawMaterialStock,
    value: any,
  ) => {
    const updated = [...formStocks];
    updated[index] = { ...updated[index], [key]: value };
    if (key === "is_default" && value === 1) {
      updated.forEach((b, idx) => {
        if (idx !== index) b.is_default = 0;
      });
    }
    setFormStocks(updated);
  };

  const handleToggleStatus = (rec: RawMaterial) => {
    const newStatus =
      rec.status === RecordStatus.ACTIVE
        ? RecordStatus.INACTIVE
        : RecordStatus.ACTIVE;
    toggleStatusMutation.mutate(
      { id: rec.id, status: newStatus },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success("Status updated successfully.");
          } else {
            message.error(res?.message || "Failed to update status.");
          }
        },
      },
    );
  };

  const handleDelete = (rec: RawMaterial) => {
    deleteMutation.mutate(rec.id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Raw material deleted successfully.");
        } else {
          message.error(res?.message || "Failed to delete raw material.");
        }
      },
    });
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Raw Materials"
        subText={`${total} materials`}
        handleButtons={[
          ...(isAllowed(RawMaterialAction.Manage)
            ? [
                {
                  title: "Add Material",
                  icon: PlusOutlined,
                  onClick: () => openDrawer(),
                },
              ]
            : []),
          { title: "", icon: ReloadOutlined, onClick: refetch },
        ]}
        searchBar={
          <RawMaterialsFilters
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            supplierId={supplierId}
            onSupplierChange={(v) => {
              setSupplierId(v);
              setPage(1);
            }}
            suppliers={suppliers}
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <RawMaterialsTable
          materialsList={materialsList?.data || []}
          total={total}
          page={page}
          isLoading={isLoading}
          onPageChange={setPage}
          onEdit={openDrawer}
          onShowHistory={(rec) => {
            setHistoryMaterial(rec);
            setHistoryDrawerOpen(true);
          }}
          onToggleStatus={handleToggleStatus}
          onDelete={handleDelete}
        />
      </div>

      <RawMaterialFormDrawer
        open={drawerOpen}
        editingMaterial={editingMaterial}
        form={form}
        formStocks={formStocks}
        suppliers={suppliers}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        onClose={closeDrawer}
        onSubmit={handleSubmit}
        onAddStock={addFormStock}
        onRemoveStock={removeFormStock}
        onUpdateStock={updateFormStock}
      />

      <StockHistoryDrawer
        open={historyDrawerOpen}
        material={historyMaterial}
        onClose={() => {
          setHistoryDrawerOpen(false);
          setHistoryMaterial(null);
        }}
      />
    </div>
  );
};
